// Cifrado opcional de la e.firma (FIEL) en el navegador antes de enviarla al API.
//
// El usuario cifra la llave privada (.key) y la contraseña como JWE compacto estándar
// (RSA-OAEP-256 + A256GCM) usando la llave pública de SatGo. Así la e.firma en claro nunca
// sale de la máquina del usuario: por la red solo viaja el ciphertext, y solo la llave privada
// de SatGo (en su Key Vault) puede abrirlo. El certificado (.cer) es público y va en claro.
//
// El cómputo corre 100% en el navegador (Web Crypto vía la librería `jose`).

import { importSPKI, CompactEncrypt } from 'jose';

const API_URL_V1 = process.env.REACT_APP_API_URL;
const ALG = 'RSA-OAEP-256';
const ENC = 'A256GCM';

// Cache a nivel de módulo: la llave pública de SatGo se pide una sola vez por sesión.
let publicKeyPromise = null;

/**
 * Obtiene (y cachea) la llave pública de cifrado de FIEL desde el API.
 * @returns {Promise<{ enabled: boolean, key?: CryptoKey, kid?: string }>}
 */
export function getFielEncryptionKey(token) {
  if (!publicKeyPromise) {
    publicKeyPromise = (async () => {
      try {
        const res = await fetch(`${API_URL_V1}/secretprovider/fiel-encryption-key`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return { enabled: false };      // 503 → feature no habilitada en el server
        const data = await res.json();
        const key = await importSPKI(data.publicKeyPem, ALG);
        console.debug(`[FIEL] llave pública de cifrado obtenida (kid=${data.kid}).`);
        return { enabled: true, key, kid: data.kid };
      } catch {
        return { enabled: false };
      }
    })();
  }
  return publicKeyPromise;
}

async function encryptBytesToJwe(key, bytes) {
  return await new CompactEncrypt(bytes)
    .setProtectedHeader({ alg: ALG, enc: ENC })
    .encrypt(key);
}

/**
 * Cifra la llave privada (.key) y la contraseña de la FIEL como JWE compacto usando la llave
 * pública de SatGo. Es el núcleo compartido por `buildFielFormData` (uso inmediato en el
 * playground) y `exportEncryptedFiel` (descarga para usar en un sistema propio).
 *
 * @returns {Promise<{ keyJwe: string, passJwe: string, kid?: string }>}
 */
export async function encryptFielCredentials({ privateKeyFile, fielPassword }, token) {
  const { enabled, key, kid } = await getFielEncryptionKey(token);
  if (!enabled) {
    throw new Error(
      'El cifrado de la e.firma no está disponible en el servidor. ' +
      'Desactiva la opción "Cifrar e.firma" o contacta a soporte.');
  }

  const keyBytes = new Uint8Array(await privateKeyFile.arrayBuffer());
  const keyJwe = await encryptBytesToJwe(key, keyBytes);
  console.debug(`[FIEL] cifrando llavePrivada: ${keyBytes.length} B → JWE ${keyJwe.length} chars.`);

  const passBytes = new TextEncoder().encode(fielPassword);
  const passJwe = await encryptBytesToJwe(key, passBytes);
  console.debug(`[FIEL] cifrando Contrasena: ${passBytes.length} B → JWE ${passJwe.length} chars.`);

  return { keyJwe, passJwe, kid };
}

/**
 * Construye el FormData de la FIEL para una petición, cifrando opcionalmente la llave y la
 * contraseña. Devuelve también los headers extra a agregar (X-Fiel-Encryption cuando aplica).
 *
 * Reglas (idénticas al flujo en claro previo):
 *   - Si el RFC tiene la FIEL registrada en Key Vault → no se envía ningún archivo.
 *   - Si `credentials.encryptFiel` está activo → llave y contraseña van como JWE; el .cer en claro.
 *   - En otro caso → llave, cert y contraseña en claro (comportamiento anterior).
 *
 * @returns {Promise<{ formData: FormData, headers: Record<string,string> }>}
 */
export async function buildFielFormData(credentials, token) {
  const fd = new FormData();
  const headers = {};

  const vaultRegistered = credentials.signingKeyStatus?.isRegistered === true;
  if (vaultRegistered) return { formData: fd, headers };

  const { privateKeyFile, certFile, fielPassword, encryptFiel } = credentials;

  if (encryptFiel) {
    const { keyJwe, passJwe } = await encryptFielCredentials({ privateKeyFile, fielPassword }, token);

    // El JWE (texto) se sube COMO el contenido del archivo; el backend lo lee como texto y descifra.
    fd.append('llavePrivada', new Blob([keyJwe], { type: 'application/jose' }), 'llave.key.jwe');
    fd.append('Certificado', certFile);   // público, en claro
    fd.append('Contrasena', passJwe);
    headers['X-Fiel-Encryption'] = 'JWE';
  } else {
    fd.append('llavePrivada', privateKeyFile);
    fd.append('Certificado', certFile);
    fd.append('Contrasena', fielPassword);
  }

  return { formData: fd, headers };
}

/**
 * Cifra la llave privada y la contraseña de la FIEL, y dispara la descarga del .key.jwe en el
 * navegador del usuario. Pensado para clientes que quieren integrar directo con la API de SatGo
 * desde su propio backend: se llevan la llave cifrada y la contraseña cifrada, y las envían tal
 * cual en los campos `llavePrivada`/`Contrasena` junto con el header `X-Fiel-Encryption: JWE`.
 * El texto en claro de la FIEL nunca sale del navegador.
 *
 * @returns {Promise<{ keyJwe: string, passJwe: string, kid?: string }>}
 */
export async function exportEncryptedFiel({ rfc, privateKeyFile, fielPassword }, token) {
  const { keyJwe, passJwe, kid } = await encryptFielCredentials({ privateKeyFile, fielPassword }, token);

  const safeRfc = (rfc || 'efirma').trim().toUpperCase().replace(/[^A-Z0-9&Ñ]/g, '') || 'efirma';
  const blob = new Blob([keyJwe], { type: 'application/jose' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeRfc}.key.jwe`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { keyJwe, passJwe, kid };
}
